import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/site/PageHero";
import TourCard from "@/components/site/TourCard";
import { Reveal } from "@/components/motion";
import { getTours, getTourCategories } from "@/lib/api";

export default function Tours() {
  const [params, setParams] = useSearchParams();
  const [tours, setTours] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const activeCat = params.get("category") || "";

  useEffect(() => {
    getTourCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (activeCat) q.category = activeCat;
    if (params.get("search")) q.search = params.get("search");
    getTours(q).then((d) => setTours(d)).catch(() => setTours([])).finally(() => setLoading(false));
  }, [activeCat, params]);

  const setCat = (c) => {
    const p = new URLSearchParams(params);
    if (c) p.set("category", c); else p.delete("category");
    setParams(p);
  };

  const onSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search) p.set("search", search); else p.delete("search");
    setParams(p);
  };

  return (
    <>
      <Seo title="India Tour Packages | Legacy India Travel" description="Explore our tailor-made India tour packages — Golden Triangle, Rajasthan, Kerala, Kashmir, Ladakh and more. No fixed prices, fully customisable. Enquire today." />
      <PageHero
        title="India Tour Packages"
        subtitle="Hand-crafted journeys across Incredible India — every itinerary tailored to you. No fixed prices, just possibilities."
        image="https://images.unsplash.com/photo-1477587458883-47145ed94245?auto=format&fit=crop&w=1600&q=80"
        crumbs={[{ label: "Tours" }]}
      />

      <section className="py-14 sm:py-20 bg-[#fdfbf7]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          {/* Search + filters */}
          <div className="flex flex-col lg:flex-row gap-6 lg:items-center justify-between mb-10">
            <div className="flex flex-wrap gap-2" data-testid="tour-filters">
              <button onClick={() => setCat("")} className={`px-4 py-2 text-xs tracking-[0.14em] uppercase border transition-colors ${!activeCat ? "bg-[#7c1d1d] text-white border-[#7c1d1d]" : "border-[#d4af37]/40 text-[#3b1a1a] hover:border-[#7c1d1d]"}`}>All</button>
              {cats.map((c) => (
                <button key={c} data-testid={`filter-${c}`} onClick={() => setCat(c)} className={`px-4 py-2 text-xs tracking-[0.14em] uppercase border transition-colors ${activeCat === c ? "bg-[#7c1d1d] text-white border-[#7c1d1d]" : "border-[#d4af37]/40 text-[#3b1a1a] hover:border-[#7c1d1d]"}`}>{c}</button>
              ))}
            </div>
            <form onSubmit={onSearch} className="flex items-center gap-2 border border-[#d4af37]/40 bg-white px-4 py-2.5 min-w-[260px]">
              <Search className="w-4 h-4 text-[#7c1d1d]" />
              <input data-testid="tour-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tours..." className="flex-1 bg-transparent outline-none text-sm text-[#3b1a1a]" />
              {search && <button type="button" onClick={() => { setSearch(""); const p = new URLSearchParams(params); p.delete("search"); setParams(p); }}><X className="w-4 h-4 text-[#5a4b4b]" /></button>}
            </form>
          </div>

          {loading ? (
            <div className="text-center py-20 text-[#5a4b4b]">Loading journeys…</div>
          ) : tours.length === 0 ? (
            <div className="text-center py-20 text-[#5a4b4b]" data-testid="tours-empty">No tours found. Try another category or search.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8" data-testid="tours-grid">
              {tours.map((t, i) => (
                <Reveal key={t.id} delay={(i % 4) * 0.05}><TourCard tour={t} /></Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
